import cx from 'classnames';
import {useMemo} from 'react';

import {TestAttribute, WithTestAttributeProps} from 'components';

import {ICONS} from './icons-export';

export type IconName = keyof typeof ICONS;

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '6xl' | '7xl' | '8xl' | 'initial' | 'full';
const sizeMap: {[key in Size]: number | string} = {
    xs: 12,
    sm: 16,
    md: 20,
    lg: 24,
    xl: 28,
    '2xl': 32,
    '3xl': 40,
    '4xl': 48,
    '6xl': 64,
    '7xl': 80,
    '8xl': 96,
    initial: 'initial',
    full: '100%',
};
type Color = 'alert' | 'alternative' | 'dark' | 'default' | 'default-dimmed' | 'light' | 'primary' | 'primary-light' | 'success' |
    'warning' ;
const colorClassMap: {[key in Color]: string} = {
    alert: 'tw-text-alert',
    alternative: 'tw-text-alternative',
    dark: 'tw-text-dark',
    default: 'tw-text-default',
    'default-dimmed': 'tw-text-default-dimmed',
    light: 'tw-text-light',
    primary: 'tw-text-primary',
    'primary-light': 'tw-text-primary-light',
    success: 'tw-text-success',
    warning: 'tw-text-warning-light',
};
type Thickness = 'none' | 'xs' | 'sm' | 'md' | 'base' | 'lg' | 'xl';
const thicknessMap: {[key in Thickness]: number} = {
    none: 0,
    xs: 0.5,
    sm: 1,
    md: 1.5,
    base: 2,
    lg: 2.5,
    xl: 3,
};

type Props = {
    name: IconName;
    button?: boolean;
    className?: string;
    color?: Color;
    primary?: boolean;
    size?: Size;
    disabled?: boolean;
    group?: boolean;
    thickness?: Thickness;
    fill?: 'none' | 'white' | 'currentColor';
    margin?: boolean;
    stopPropagation?: boolean;
    [key: string]: any;
} & WithTestAttributeProps;

export type IconSize = Size;
export type IconColor = Color;
export type IconThickness = Thickness;
export type IconProps = Props;

export const Icon: React.FC<Props> = ({
    name, button, group, size: sizeName = 'md', thickness = 'md', margin = true,
    color = 'default', fill = 'none', className, disabled, primary, stopPropagation, testAttribute, ...rest
}: Props) => {
    const IconComponent = useMemo(() => ICONS[name], [name]);
    const size = useMemo(() => sizeMap[sizeName], [sizeName]);
    const strokeWidth = useMemo(() => thicknessMap[thickness], [thickness]);
    const colorClassName = useMemo(() => colorClassMap[primary ? 'primary' : color], [color, primary]);
    return (
        <TestAttribute testAttribute={{...(testAttribute && testAttribute), skipIsHTMLChild: true}}>
            <IconComponent
                {...rest}
                {...(sizeName !== 'initial' && {
                    size,
                    width: size,
                    height: size
                })}
                fill={fill}
                stroke='currentColor'
                strokeWidth={strokeWidth}
                className={cx(className, 'tw-flex-shrink-0', colorClassName, {
                    'tw-cursor-pointer': button && !disabled,
                    'tw-mr-2 last:tw-mr-0': button && group && margin,
                    'tw-mx-auto': button && !group && margin,
                    'tw-opacity-20': disabled
                })}
                {...stopPropagation && rest.onClick && {
                    onClick: (e) => {
                        e.stopPropagation();
                        rest.onClick(e);
                    }
                }}
                {...(disabled && {onClick: null})}
            />
        </TestAttribute>
    );
};
